import { ReactNode } from 'react';
import styled, { type CSSProp, css } from 'styled-components/macro';

export const Wrapper = styled.div<{
  $width?: string;
  $margin?: string;
  $backgroundColor?: string;
  $flexDirection?: string;
  $isVisible?: boolean;
  placeholderColor?: string;
}>`
  position: relative;

  display: flex;
  ${({ $isVisible }) => ($isVisible === false ? 'display: none' : 'display: flex')};

  ${({ placeholderColor }) =>
    placeholderColor &&
    `

/* Chrome, Safari, Edge */
input:-webkit-autofill {
  -webkit-box-shadow: 0 0 0px 1000px white inset; /* or background color you want */
  -webkit-text-fill-color: black; /* text color */
  transition: background-color 5000s ease-in-out 0s; /* optional hack to prevent flicker */
}

/* Firefox (recent versions) */
input:-moz-autofill {
  box-shadow: 0 0 0px 1000px white inset;
  -moz-text-fill-color: black;
}

  input::placeholder {
    color: ${placeholderColor}}; 
    opacity: 1; 
  }

  input::-webkit-input-placeholder {
       color: ${placeholderColor}}; 

  }

  input::-moz-placeholder {
     color: ${placeholderColor}}; 
  opacity: 1;
}
  `};

  flex-direction: ${({ $flexDirection }) => ($flexDirection ? $flexDirection : 'column')};
  align-items: ${({ $flexDirection }) => $flexDirection && 'center'};

  width: ${({ $width }) => ($width ? $width : '100%')};
  ${({ $backgroundColor }) => $backgroundColor && `background-color: ${$backgroundColor};`}
  margin: ${({ theme, $margin }) => (!!$margin ? $margin : `0 0 ${theme.spacing.one_1} 0 `)};
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 100%;
  }
`;

export const Label = styled.label<{
  $hasError?: boolean;
  $tooltip?: ReactNode;
  $justifyContent?: string;
  $fontSize?: string;
  $paddingLabel?: string;
  $visibility?: string;
}>`
  display: flex;

  user-select: none;

  ${({ $tooltip }) => $tooltip && ` align-items: center; `}
  ${({ $justifyContent }) => $justifyContent && ` justify-content: ${$justifyContent}; `}

  padding: ${({ $paddingLabel, theme }) =>
    $paddingLabel
      ? $paddingLabel
      : `${theme.spacing.zero} ${theme.spacing.zero} ${theme.spacing.zero_2} ${theme.spacing.zero}`};

  color: ${({ theme, $hasError }) =>
    $hasError ? theme.colors.negative_dark : theme.colors.black_80};

  font-size: ${({ $fontSize, theme }) => ($fontSize ? $fontSize : theme.fontSize.xxs)};
  line-height: ${({ theme }) => theme.lineHeight.one};

  ${({ $visibility }) => $visibility && ` visibility: ${$visibility}; `}
`;

export const Error = styled.span<{ margin?: string | null }>`
  color: ${({ theme }) => theme.colors.negative_dark};

  display: block;

  font-size: ${({ theme }) => theme.fontSize.xxs};
  line-height: ${({ theme }) => theme.lineHeight.zero_3};
  padding: ${({ theme }) => theme.spacing.zero_1} 0;
  // to prevent moving next element by error message height

  margin: ${({ margin, theme }) => (margin ? margin : `margin-bottom: -${theme.spacing.one_1}; `)};
`;

export const Asterisk = styled.i<{ $isDisabled?: boolean }>`
  color: ${({ theme, $isDisabled }) =>
    $isDisabled ? theme.colors.black_40 : theme.colors.negative_dark_1};
  font-style: normal;
`;

const InputWrapperStyle = css<{
  $hasError?: boolean;
  $isDisabled?: boolean;
  $borderTopRightRadius?: boolean;
  $borderBottomRightRadius?: boolean;
  icon?: ReactNode;
  $border?: string;
  $padding?: string;
  $disableCursorOnly?: boolean;
  $disableFocusWithin?: string;
  $minNumber?: number;
  $width?: string;
}>`
  display: flex;
  align-items: center;

  background-color: ${({ theme }) => theme.colors.white};

  color: ${({ theme, $hasError }) => ($hasError ? theme.colors.negative_dark : theme.colors.black)};

  width: ${({ $width }) => ($width ? $width : '100%')};
  height: ${({ theme }) => theme.spacing.two_2};

  ${({ $border, theme, $hasError }) =>
    $border
      ? `border: ${$border};`
      : ` border: 1px solid
  ${$hasError ? theme.colors.negative_dark : theme.colors.black_20};`}

  border-radius: ${({ theme }) => theme.spacing.zero_1};

  ${({ $borderTopRightRadius }) => $borderTopRightRadius && ` border-top-right-radius: 0;`}
  ${({ $borderBottomRightRadius }) => $borderBottomRightRadius && ` border-bottom-right-radius: 0;`}
    ${({ $disableFocusWithin, theme, $hasError }) =>
    !$disableFocusWithin &&
    `:focus-within {
  border: 1px solid
    ${$hasError ? theme.colors.negative_dark : theme.colors.black};
}`}

    ${({ theme, $isDisabled }) =>
    $isDisabled &&
    css`
      background-color: ${theme.colors.black_5};
      cursor: not-allowed;
      pointer-events: none;

      & > input {
        color: ${theme.colors.black_60};
      }
    `}

    ${({ $disableCursorOnly }) =>
    $disableCursorOnly &&
    css`
      cursor: not-allowed;
      pointer-events: none;
    `}

    & > input {
    flex: 1 1 auto;

    height: 100%;

    outline: none;

    ${({ theme, $minNumber }) =>
      $minNumber! < 1 ? `color: ${theme.colors.black_60};` : `${theme.colors.black_100};`}

    ${({ theme, icon }) => icon && `position: relative; z-index: ${theme.zIndex.zIndex1}; `}

    border: 0 none;
    border-radius: ${({ theme }) => theme.spacing.zero_1};
    background-color: ${({ theme }) => theme.colors.transparent};
    width: 20px;

    ${({ $padding, theme }) => `padding: ${$padding ? $padding : theme.spacing.zero_3}`};

    :disabled {
      color: ${({ theme }) => theme.colors.black_40};
    }

    input[type='time']:not(.has-value):before {
      color: ${({ theme }) => theme.colors.black_60};
      content: attr(placeholder);
    }

    ::-webkit-calendar-picker-indicator {
      background: none;
    }

    ::-ms-reveal {
      display: none;
    }
  }
`;

export const InputWrapperLabel = styled.label<{
  $hasError?: boolean;
  $isDisabled?: boolean;
  $borderTopRightRadius?: boolean;
  $borderBottomRightRadius?: boolean;
  $border?: string;
  $padding?: string;
  $disableCursorOnly?: boolean;
  icon?: ReactNode;
  $extendedStyles?: CSSProp;
}>`
  ${InputWrapperStyle};

  ${({ $extendedStyles }) =>
    $extendedStyles &&
    css`
      ${$extendedStyles}

      > input {
        background-color: transparent !important;
      }

      :focus-within {
        border: none;
        border-bottom: 1px solid #000000;
      }
    `};
`;

export const InputWrapperDiv = styled.div<{
  $hasError?: boolean;
  $isDisabled?: boolean;
  $minNumber?: number;
  $width?: string;
  $margin?: string;
  $backgroundColor?: string;
  $flexDirection?: string;
  $border?: string;
  $disableFocusWithin?: string;
}>`
  ${InputWrapperStyle};
  padding: ${({ theme }) => theme.spacing.zero_3};
`;

export const InputIcon = styled.div<{
  $hasError?: boolean;
  $isDisabled?: boolean;
  $hasIconAction?: boolean;
  width?: string;
  icon?: ReactNode;
  $inputIconRounded?: boolean;
  $inputIconBackgroundColor?: boolean;
}>`
  display: flex;

  align-items: center;
  justify-content: center;

  :first-child {
    background-color: ${({ theme, $inputIconBackgroundColor }) =>
      $inputIconBackgroundColor ? theme.colors.black_10 : 'transparent'};
  }

  flex: 0 1 auto;

  ${({ theme, icon }) => icon && `position: absolute; right: ${theme.spacing.zero_3}; `}

  ${({ width }) => width && `width: ${width || '34px'}; `}
  
  ${({ theme, $inputIconRounded }) =>
    $inputIconRounded &&
    `border-radius: 6.25rem; padding: 0.125rem; border: 1px solid ${theme.colors.black_20}; `}

  ${({ onClick }) =>
    !!onClick &&
    css`
      :hover {
        cursor: pointer;
      }
    `}
`;

export const InputRightSide = styled.div`
  margin-right: ${({ theme }) => theme.spacing.zero_2};
`;

export const InputLeftSide = styled.div`
  display: flex;
  align-items: center;
  padding-left: 12px;
  padding-right: 2px;
  white-space: nowrap;
`;
